import { useEffect, useState, type FormEvent } from 'react'
import { createAssessment, listAllocations, listAssessments } from '@/lib/api'
import { useAsyncData } from '@/lib/useAsyncData'

export function LecturerAssessmentsPage() {
  const allocations = useAsyncData(listAllocations, [])
  const assessments = useAsyncData(listAssessments, [])
  const [allocationId, setAllocationId] = useState('')
  const [title, setTitle] = useState('')
  const [type, setType] = useState('QUIZ')
  const [maxMarks, setMaxMarks] = useState('20')
  const [weight, setWeight] = useState('10')
  const [dateHeld, setDateHeld] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!allocationId && (allocations.data || [])[0]) {
      setAllocationId(String((allocations.data || [])[0].id))
    }
  }, [allocations.data, allocationId])

  async function onCreate(e: FormEvent) {
    e.preventDefault()
    if (!allocationId || !title.trim()) return
    setBusy(true)
    setMessage(null)
    try {
      await createAssessment({
        course_allocation: Number(allocationId),
        title: title.trim(),
        assessment_type: type,
        max_marks: maxMarks,
        weight_percentage: weight,
        date_held: dateHeld || null,
      })
      setTitle('')
      setMessage('Assessment created.')
      await assessments.reload()
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6 p-8">
      <h1 className="text-2xl font-bold text-slate-800">Assessments</h1>
      {message && <p className="rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-700">{message}</p>}

      <form onSubmit={onCreate} className="grid gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm md:grid-cols-2">
        <select
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          value={allocationId}
          onChange={(e) => setAllocationId(e.target.value)}
        >
          {(allocations.data || []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.course_details.code} — {a.course_details.title}
            </option>
          ))}
        </select>
        <input
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <select
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="QUIZ">Quiz</option>
          <option value="TEST">Test</option>
          <option value="MID_SEMESTER">Mid Semester</option>
          <option value="FINAL_EXAM">Final Exam</option>
        </select>
        <input
          type="date"
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          value={dateHeld}
          onChange={(e) => setDateHeld(e.target.value)}
        />
        <input
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="Max marks"
          value={maxMarks}
          onChange={(e) => setMaxMarks(e.target.value)}
        />
        <input
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="Weight %"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white md:col-span-2"
        >
          {busy ? 'Saving…' : 'Create assessment'}
        </button>
      </form>

      <div className="space-y-3">
        {(assessments.data || []).map((a) => (
          <div key={a.id} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
            <p className="text-sm font-bold text-slate-800">{a.title}</p>
            <p className="text-xs text-slate-500">
              {a.course_code} · {a.assessment_type} · {a.max_marks} marks · weight {a.weight_percentage}%
              {a.date_held ? ` · ${a.date_held}` : ''}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
